import React, { useLayoutEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import artwork from './assets/cover-artwork.svg';
import applicatorArtwork from './assets/applicator/cover-artwork.svg';
import { formatCpf } from './IdentificationForm';
import './exam-covers.css';

export const COVER_CONTENT = {
  instructions: [
    'Leia com atenção antes de responder e marque suas respostas neste caderno;',
    'Cada questão tem uma única resposta correta.',
    'Responda com calma e procure não deixar questão sem resposta.',
    'Reserve os últimos 20 minutos para transcrever suas respostas para a Folha de Respostas.',
    'Cuidado e muita atenção com a ordem das questões para fazer a marcação.',
    'Quando for autorizado, transcreva suas respostas para a Folha de Respostas, utilizando caneta de tinta azul ou preta.',
    'Ao final, você poderá levar este simulado para casa.'
  ]
};

// The artwork is shared; student data and QR stay in the overlay.
export const ExamCover = React.memo(function ExamCover({ studentId, student, school, title, description, loginUrl, content = COVER_CONTENT, applicator = false }) {
  const cpf = student?.cpf === '00000000000' || student?.cpf === '000.000.000-00' ? '' : formatCpf(student?.cpf);
  const headingRef = useRef(null);

  useLayoutEffect(() => {
    const heading = headingRef.current;
    let disposed = false;
    const fitText = () => {
      if (disposed) return;
      for (const field of heading.children) {
        field.style.fontSize = '';
        field.style.webkitLineClamp = 'unset';
        let size = parseFloat(getComputedStyle(field).fontSize);
        if (field.tagName === 'P') {
          const titleSize = parseFloat(getComputedStyle(heading.firstElementChild).fontSize);
          size = Math.min(size, titleSize * 0.7);
          field.style.fontSize = `${size}px`;
        }
        // Measure the full text so the two-line clamp never hides its ending.
        while (field.scrollHeight > parseFloat(getComputedStyle(field).lineHeight) * 2 + 1 && size > 1) {
          size = Math.max(1, size - 0.5);
          field.style.fontSize = `${size}px`;
        }
        field.style.webkitLineClamp = '';
      }
      // Keep the four lines inside the area available on each cover variant.
      const availableHeight = heading.parentElement.clientWidth * (applicator ? 0.26 : 0.185);
      const contentHeight = () =>
        Array.from(heading.children).reduce((total, field) => {
          const style = getComputedStyle(field);
          return total + field.getBoundingClientRect().height + (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
        }, 0);
      // For long titles, use the spare description space before shrinking the title.
      const descriptionField = heading.querySelector('p');
      if ((title?.length || 0) >= 55 && descriptionField && availableHeight > 0) {
        let size = parseFloat(getComputedStyle(descriptionField).fontSize);
        const minimumSize = heading.parentElement.clientWidth * 0.022;
        while (contentHeight() > availableHeight && size > minimumSize) {
          size = Math.max(minimumSize, size - 0.5);
          descriptionField.style.fontSize = `${size}px`;
        }
      }
      const height = contentHeight();
      if (height > availableHeight && availableHeight > 0) {
        const scale = availableHeight / height;
        for (const field of heading.children) {
          field.style.fontSize = `${parseFloat(getComputedStyle(field).fontSize) * scale}px`;
        }
      }
    };
    fitText();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fitText) : null;
    observer?.observe(heading.parentElement);
    document.fonts?.ready.then(fitText);
    return () => {
      disposed = true;
      observer?.disconnect();
    };
  }, [title, description, applicator]);
  return (
    <article
      className={applicator ? 'exam-cover exam-cover--applicator' : 'exam-cover'}
      aria-label={applicator ? 'Capa do caderno do aplicador' : `Capa da prova do aluno ${studentId}`}
    >
      <img
        className="exam-cover__artwork"
        src={applicator ? applicatorArtwork : artwork}
        alt=""
        width="595"
        height="842"
      />
      {applicator && <p className="exam-cover__badge">CADERNO DO APLICADOR</p>}
      <header
        ref={headingRef}
        className="exam-cover__heading"
      >
        <h1 title={title}>{title}</h1>
        {description && <p title={description}>{description}</p>}
      </header>
      {!applicator && (
        <section
          className="exam-cover__student"
          aria-label="Dados do aluno"
        >
          <div className="exam-cover__student-row">
            <p className="exam-cover__student-name">Nome{student?.name ? `: ${student.name}` : ''}</p>
            <p>CPF{cpf ? `: ${cpf}` : ''}</p>
          </div>
          <p>Escola{school ? `: ${school}` : ''}</p>
        </section>
      )}
      <section
        className="exam-cover__instructions"
        aria-label="Orientações da prova"
      >
        <p>
          <strong>Caro(a) estudante,</strong>
        </p>
        <p>
          Você está participando do <strong>{title}.</strong> Sua participação é muito importante para sabermos como está a nossa educação. Abaixo você encontrará algumas orientações:
        </p>
        <ul>
          {content.instructions.map(instruction => (
            <li key={instruction}>{instruction}</li>
          ))}
        </ul>
        <p>Bom simulado!</p>
      </section>
      {!applicator && loginUrl && (
        <div className="exam-cover__access">
          <div className="exam-cover__qr">
            <QRCodeSVG
              value={loginUrl}
              size={128}
              level="M"
              marginSize={2}
              title={`Acesso à plataforma para o aluno ${studentId}`}
            />
          </div>
          <p>Aponte a câmera do seu celular para o QR Code e acesse a plataforma.</p>
        </div>
      )}
    </article>
  );
});
